import React, { useState, useEffect, useRef, useMemo } from 'react';

const SUMMIT_M    = 4810;          // Mont Blanc summit
const ANIM_MAX_MS = 5000;
const ANIM_MIN_MS = 400;

// Trois Monts route from Cosmiques Hut over Mont Blanc du Tacul and
// Mont Maudit, through Col de la Brenva and up Mur de la Côte to the
// summit. The `t` value is each waypoint's fractional position along
// the SVG route — placement follows real route distance, not altitude,
// so the line can dip across Col de la Brenva like the real ridge.
// Above Col de la Brenva the route is split into three staircase
// segments: the steep Mur de la Côte to the Petits Rochers Rouges,
// the sustained middle slopes to the Rochers des Petits Mulets, then
// the gentle summit dome to the top.
const WAYPOINTS = [
  { name: 'Cosmiques Hut',         alt: 3613, t: 0.00, labelAnchor: 'start',  labelDx:  10, labelDy:  16 },
  { name: 'Mont Blanc du Tacul',   alt: 4248, t: 0.31, labelAnchor: 'start',  labelDx:  10, labelDy:  -8 },
  { name: 'Mont Maudit',           alt: 4465, t: 0.48, labelAnchor: 'middle', labelDx:   0, labelDy: -10 },
  { name: 'Col de la Brenva',      alt: 4303, t: 0.60, labelAnchor: 'middle', labelDx:   0, labelDy:  22 },
  { name: 'Petits Rochers Rouges', alt: 4500, t: 0.69, labelAnchor: 'start',  labelDx:   8, labelDy:  14 },
  { name: 'Petits Mulets',         alt: 4690, t: 0.78, labelAnchor: 'end',    labelDx:  -8, labelDy: -10 },
  { name: 'Mont Blanc',            alt: 4810, t: 1.00, labelAnchor: 'end',    labelDx: -12, labelDy:  18 },
];

const wpPts = (wp, target) => Math.round(wp.t * target);

// Trois Monts route profile (SVG coords). y coords scale altitude:
// y = 285 - (alt - 3613) * (255 / 1197). x coords are compressed across
// the Cosmiques → Maudit section so the climber reaches the three
// summits quickly, leaving the upper half for the staircase push above
// Col de la Brenva: a steep Mur de la Côte to the Petits Rochers
// Rouges, a moderate middle ramp to the Petits Mulets, then a gentle
// summit dome to the top.
const ROUTE_D =
  'M 40 285 C 100 280, 180 220, 250 150 ' +
  'C 300 125, 350 108, 380 104 ' +
  'C 405 115, 445 132, 470 138 ' +
  'C 485 130, 510 105, 525 90 ' +
  'C 545 78, 570 62, 590 55 ' +
  'C 640 45, 720 33, 770 30';

export default function MountainProgress({ points = 0, target = 100_000 }) {
  const pathRef = useRef(null);
  const [pathLen, setPathLen] = useState(0);
  const [animatedPts, setAnimatedPts] = useState(0);
  const [replayTick, setReplayTick] = useState(0);

  useEffect(() => {
    if (pathRef.current) setPathLen(pathRef.current.getTotalLength());
  }, []);

  // Replay the full 0 → target climb on each points change and on replay click.
  useEffect(() => {
    if (points === null || points === undefined) return;
    const target = Math.max(0, points);

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setAnimatedPts(target);
      return;
    }

    const duration = Math.min(
      ANIM_MAX_MS,
      Math.max(ANIM_MIN_MS, (target / target) * ANIM_MAX_MS),
    );

    setAnimatedPts(0);
    const startTime = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimatedPts(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [points, replayTick]);

  const progress = Math.min(1, Math.max(0, animatedPts / target));

  const climber = useMemo(() => {
    if (!pathRef.current || pathLen === 0) return null;
    return pathRef.current.getPointAtLength(pathLen * progress);
  }, [progress, pathLen]);

  const waypointPoints = useMemo(() => {
    if (!pathRef.current || pathLen === 0) return [];
    return WAYPOINTS.map((wp) => {
      const p = pathRef.current.getPointAtLength(pathLen * wp.t);
      return { ...wp, x: p.x, y: p.y };
    });
  }, [pathLen]);

  // Altitude follows the actual route profile — interpolated linearly
  // between adjacent waypoints by route position. This means the readout
  // dips when the climber drops from Mt Maudit into Col de la Brenva,
  // then climbs again up the Mur de la Côte to the summit.
  let currentAlt = SUMMIT_M;
  for (let i = 0; i < WAYPOINTS.length - 1; i++) {
    const a = WAYPOINTS[i];
    const b = WAYPOINTS[i + 1];
    if (progress >= a.t && progress <= b.t) {
      const f = (progress - a.t) / (b.t - a.t);
      currentAlt = Math.round(a.alt + (b.alt - a.alt) * f);
      break;
    }
  }

  const atSummit = progress >= 1;
  const summitWp = waypointPoints[waypointPoints.length - 1];
  const targetPts = Math.max(0, points || 0);

  // Subtle walking tilt — the climber rocks ±2° around the foot as it
  // moves up the route. ~30 cycles span the full climb so during the
  // 5 s animation the rock matches a brisk walking cadence.
  const STEP_CYCLES = 30;
  const tiltDeg     = Math.sin(progress * STEP_CYCLES * 2 * Math.PI) * 2;

  return (
    <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-[#e6edf3] text-sm font-semibold">Mont Blanc</h3>
        <span className="text-[#e6edf3] text-sm font-semibold text-right">4,810 m</span>
      </div>

      <button
        type="button"
        onClick={() => setReplayTick((t) => t + 1)}
        className="block w-full rounded border border-[#30363d] bg-[#0d1117] overflow-hidden text-left hover:border-[#d29922]/40 transition-colors"
        aria-label="Replay climb animation"
      >
        <svg viewBox="0 0 800 320" className="block w-full h-auto">
          <defs>
            <linearGradient id="mb-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#0d1b2a" />
              <stop offset="100%" stopColor="#1b263b" />
            </linearGradient>
            <linearGradient id="mb-snow" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#f0f6fc" />
              <stop offset="100%" stopColor="#8b949e" />
            </linearGradient>
            <linearGradient id="mb-rock" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#484f58" />
              <stop offset="100%" stopColor="#161b22" />
            </linearGradient>
          </defs>

          <rect x="0" y="0" width="800" height="320" fill="url(#mb-sky)" />

          {/* Back ridge */}
          <path
            d="M 0 230 L 120 180 L 250 200 L 420 130 L 580 170 L 800 110 L 800 320 L 0 320 Z"
            fill="#1b263b"
            opacity="0.55"
          />

          {/* Trois Monts ridge — quick approach to Tacul/Maudit, dip at
              Col de la Brenva, then the staircase above: steep Mur de
              la Côte, moderate middle slopes, gentle summit dome */}
          <path
            d="M 0 320 L 0 295 L 40 290 L 130 255 L 200 200 L 250 152 L 320 130 L 380 106 L 420 120 L 470 138 L 525 90 L 590 55 L 700 35 L 770 30 L 800 38 L 800 320 Z"
            fill="url(#mb-rock)"
          />

          {/* Snow cap — covers the alpine ridge from the Tacul shoulder
              upward */}
          <path
            d="M 250 152 L 320 130 L 380 106 L 420 120 L 470 138 L 525 90 L 590 55 L 700 35 L 770 30 L 800 38 L 800 158 L 250 158 Z"
            fill="url(#mb-snow)"
          />

          {/* Route line */}
          <path
            ref={pathRef}
            d={ROUTE_D}
            fill="none"
            stroke="#d29922"
            strokeWidth="2"
            strokeDasharray="6 4"
            opacity="0.8"
          />

          {/* Waypoints */}
          {waypointPoints.map((wp) => {
            const passed     = progress >= wp.t;
            const isSummit   = wp.t === 1;
            const wpPoints   = wpPts(wp, target);
            const extraPts   = wpPoints - targetPts;
            const isUnreached = extraPts > 0;
            return (
              <g key={wp.name}>
                <circle
                  cx={wp.x}
                  cy={wp.y}
                  r={isSummit ? 6 : 4.5}
                  fill={passed ? '#d29922' : '#30363d'}
                  stroke="#0d1117"
                  strokeWidth="1.5"
                />
                {/* Name (outlined for legibility over sky and snow) */}
                <text
                  x={wp.x + wp.labelDx}
                  y={wp.y + wp.labelDy}
                  fontSize="13"
                  fontFamily="monospace"
                  textAnchor={wp.labelAnchor}
                  stroke="#0d1117"
                  strokeWidth="3.5"
                  strokeLinejoin="round"
                  style={{ paintOrder: 'stroke' }}
                  fill={passed ? '#d29922' : '#e6edf3'}
                >
                  {wp.name}
                </text>
                {/* Altitude line */}
                <text
                  x={wp.x + wp.labelDx}
                  y={wp.y + wp.labelDy + 12}
                  fontSize="10"
                  fontFamily="monospace"
                  textAnchor={wp.labelAnchor}
                  stroke="#0d1117"
                  strokeWidth="3"
                  strokeLinejoin="round"
                  style={{ paintOrder: 'stroke' }}
                  fill="#8b949e"
                >
                  {wp.alt.toLocaleString()} m
                </text>
                {/* Extra points needed to reach this landmark */}
                {isUnreached && (
                  <text
                    x={wp.x + wp.labelDx}
                    y={wp.y + wp.labelDy + 24}
                    fontSize="10"
                    fontFamily="monospace"
                    textAnchor={wp.labelAnchor}
                    stroke="#0d1117"
                    strokeWidth="3"
                    strokeLinejoin="round"
                    style={{ paintOrder: 'stroke' }}
                    fill="#d29922"
                  >
                    +{extraPts.toLocaleString()} pts
                  </text>
                )}
                <title>
                  {wp.name} — {wp.alt.toLocaleString()} m ({wpPoints.toLocaleString()} pts)
                  {isUnreached ? ` — ${extraPts.toLocaleString()} more pts needed` : ''}
                </title>
              </g>
            );
          })}

          {/* Summit flag once reached */}
          {atSummit && summitWp && (
            <g>
              <line
                x1={summitWp.x}
                y1={summitWp.y - 4}
                x2={summitWp.x}
                y2={summitWp.y - 28}
                stroke="#d29922"
                strokeWidth="1.6"
              />
              <polygon
                points={`${summitWp.x},${summitWp.y - 28} ${summitWp.x + 14},${summitWp.y - 25} ${summitWp.x},${summitWp.y - 22}`}
                fill="#d29922"
              />
            </g>
          )}

          {/* Hiker icon — backpack, bent-forward torso, trekking pole.
              Whole figure rocks ±2° while moving. */}
          {climber && (
            <g transform={`translate(${climber.x}, ${climber.y}) rotate(${tiltDeg})`}>
              {/* Backpack — behind the torso */}
              <rect
                x="-4.6" y="-17.5" width="4" height="8.5" rx="1.2"
                fill="#e6edf3" stroke="#0d1117" strokeWidth="0.4"
              />
              {/* Back leg — hip → knee → foot */}
              <line x1="2.5" y1="-10" x2="-1.5" y2="-5" stroke="#e6edf3" strokeWidth="2" strokeLinecap="round" />
              <line x1="-1.5" y1="-5" x2="-2.5" y2="0" stroke="#e6edf3" strokeWidth="2" strokeLinecap="round" />
              {/* Back arm */}
              <line x1="1" y1="-17.5" x2="-2" y2="-12" stroke="#e6edf3" strokeWidth="1.6" strokeLinecap="round" />
              {/* Torso — bent slightly forward */}
              <line x1="1" y1="-18" x2="2.5" y2="-10" stroke="#e6edf3" strokeWidth="2.6" strokeLinecap="round" />
              {/* Head */}
              <circle cx="1" cy="-21" r="2.8" fill="#e6edf3" stroke="#0d1117" strokeWidth="0.5" />
              {/* Front leg — hip → knee → foot */}
              <line x1="2.5" y1="-10" x2="5" y2="-5" stroke="#e6edf3" strokeWidth="2" strokeLinecap="round" />
              <line x1="5" y1="-5" x2="5" y2="0" stroke="#e6edf3" strokeWidth="2" strokeLinecap="round" />
              {/* Front arm — holds the trekking pole */}
              <line x1="1" y1="-17.5" x2="7" y2="-10" stroke="#e6edf3" strokeWidth="1.6" strokeLinecap="round" />
              {/* Trekking pole — front hand down to the ground */}
              <line x1="7" y1="-10" x2="9" y2="1" stroke="#e6edf3" strokeWidth="0.9" strokeLinecap="round" />
            </g>
          )}
        </svg>

        <div className="px-3 py-2 flex justify-between items-center text-[11px] font-mono">
          <span
            className="text-[#e6edf3] tabular-nums"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {currentAlt.toLocaleString()} m
          </span>
          <span
            className="text-[#d29922] tabular-nums"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {Math.round(progress * 100)}% complete
          </span>
        </div>
      </button>
    </div>
  );
}
